import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import { type ComponentProps, type ReactNode, useId } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";

type SwitchFieldBaseProps = Omit<
  ComponentProps<typeof Switch>,
  "checked" | "onCheckedChange" | "onChange"
> & {
  label?: ReactNode;
  description?: ReactNode;
  /** Fired AFTER the value is committed — for side effects only. */
  onValueChange?: (checked: boolean) => void;
};

export type SwitchFieldProps = SwitchFieldBaseProps & FieldBindingProps;

export function SwitchField(props: SwitchFieldProps) {
  const [binding, rest] = splitBinding(props);
  const { label, description, className, onValueChange, ...switchProps } = rest;
  const switchId = useId();
  return (
    <PolyField<boolean> {...binding}>
      {(field) => (
        <FieldWrapper
          description={description}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={switchProps.disabled}
        >
          <div className="flex items-center gap-2">
            <Switch
              id={switchId}
              {...switchProps}
              checked={Boolean(field.state.value)}
              onCheckedChange={(checked) => {
                commitFieldValue(field, checked);
                onValueChange?.(checked);
              }}
            />
            {label && (
              <Label htmlFor={switchId} className="cursor-pointer">
                {label}
              </Label>
            )}
          </div>
        </FieldWrapper>
      )}
    </PolyField>
  );
}
